import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';

import ContextMissionDetails from '../organisms/ContextMissionDetails';
import {
  BrowserBloomer,
  GqlAccountManager,
  IContact,
  IMissionCardDetails,
  ISociety,
  GetOneFullMissionResponse,
  GetOneFullMissionParams,
} from '@bloomays-lib/types.shared';
import { GET_ONE_FULL_MISSION } from '@bloomays-lib/adapter.api-bloomer';
import { errorLogger } from '../../helpers/error';
import { getFirstValidPrice, greaterDate, getLegalContactSourceI18nKey } from '@bloomays-lib/utils.shared';
import { notify } from '../../helpers/toastify';
import { LoaderPending, WithErrorBoundary } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';

interface FormData {
  bloomer?: BrowserBloomer;
  missionJobOwner?: GqlAccountManager | null;
  clientSociety?: ISociety;
  clientBillingSociety?: ISociety;
  contextContactBillingClient?: IContact | IContact[];
  bloomerSociety?: ISociety;
  contactBloomerSociety?: IContact;
  contactOperations?: IContact;
  legalClientContact?: IContact;
  legalClientSource?: string;
  legalBloomerContact?: IContact;
  legalBloomerSource?: string;
  mission?: IMissionCardDetails;
}

const MissionDetails = () => {
  const { t } = useTranslation(['random', 'mission']);
  const { id } = useParams<{ id: string }>();

  if (!id) {
    throw new Error('Missing ID params');
  }

  const {
    loading,
    error,
    data: dataMission,
  } = useQuery<GetOneFullMissionResponse, GetOneFullMissionParams>(GET_ONE_FULL_MISSION, {
    variables: {
      recordId: id,
    },
  });

  const [data, setData] = useState<FormData>();

  React.useEffect(() => {
    if (!dataMission) return;
    const mission = dataMission?.missions?.[0];
    if (!mission) return;
    const missionCardDetails = { ...mission, shouldBeCountAsDeal: true } as unknown as IMissionCardDetails;

    const activePricing = getFirstValidPrice(
      [...(mission.pricing || [])],
      greaterDate(new Date(mission.beginMission as string), new Date()),
    );
    missionCardDetails.purchasePricePerDayWithoutTaxes = activePricing?.purchasePricePerDayWithoutTaxes;
    missionCardDetails.salePricePerDayWithoutTaxes = activePricing?.salePricePerDayWithoutTaxes;
    missionCardDetails.taxes = activePricing?.taxes;
    missionCardDetails.CSM = mission.CSM?.fullname || 'Non attribué';
    missionCardDetails.prices = mission.pricing?.map((price) => ({
      ...price,
      startDate: price.startDate ? new Date(price.startDate) : undefined,
    }));
    missionCardDetails.hoursPerDays = mission.hoursPerDays;
    // Champs TEC-2888 exposés via le résolveur IMission (BDC + flags).
    missionCardDetails.purchaseOrderRequired = (mission as unknown as IMissionCardDetails).purchaseOrderRequired;
    missionCardDetails.effectivePurchaseOrderRequired = (
      mission as unknown as IMissionCardDetails
    ).effectivePurchaseOrderRequired;
    missionCardDetails.purchaseOrders = (mission as unknown as IMissionCardDetails).purchaseOrders;

    setData((prevData) => ({
      ...prevData,
      bloomer: mission?.bloomer,
      missionJobOwner: mission.jobOwner ?? null,
      clientSociety: mission?.clientSociety,
      clientBillingSociety: mission?.clientBillingSociety,
      contextContactBillingClient: mission?.billingContacts || mission?.clientSociety?.billingContact,
      bloomerSociety: mission?.bloomerBillingSociety?.[0],
      contactBloomerSociety: mission?.bloomerBillingSociety?.[0]?.billingContact,
      contactOperations: mission?.contactOperations,
      legalClientContact: mission?.resolvedClientPdsContact?.contact,
      legalClientSource: mission?.resolvedClientPdsContact?.source,
      legalBloomerContact: mission?.resolvedSupplierSstContact?.contact,
      legalBloomerSource: mission?.resolvedSupplierSstContact?.source,
      mission: missionCardDetails,
    }));
  }, [dataMission]);

  React.useEffect(() => {
    if (error) {
      errorLogger(error, {
        extraInfos: 'Internal server error, impossible to retrieve data about contact bloomer / mission !',
        state: {},
      });
      notify(
        'error',
        'Impossible de récupérer les données de mission. Erreur du server, essaie plus tard ! 😓 ',
        error,
        undefined,
      );
    }
  }, [error]);

  if (loading || !data?.mission?.recordId) return <LoaderPending text={t('loadingText')} />;

  return (
    <ContextMissionDetails
      contextBloomer={data.bloomer as BrowserBloomer}
      missionJobOwner={data.missionJobOwner ?? null}
      contextMission={data.mission}
      contextContactBloomer={data.contactBloomerSociety as IContact}
      contextContactBillingClient={data.contextContactBillingClient as IContact | IContact[]}
      contextSocietyBloomer={data.bloomerSociety as ISociety}
      contextSocietyClient={data.clientSociety as ISociety}
      contextSocietyClientBilling={data.clientBillingSociety as ISociety}
      contextContactOperationnal={data.contactOperations as IContact}
      contextLegalClientContact={data.legalClientContact}
      contextLegalClientSourceLabel={t(getLegalContactSourceI18nKey(data.legalClientSource), { ns: 'mission' })}
      contextLegalBloomerContact={data.legalBloomerContact}
      contextLegalBloomerSourceLabel={t(getLegalContactSourceI18nKey(data.legalBloomerSource), { ns: 'mission' })}
      legalContactUnavailableText={t('notAvailable', { ns: 'mission' })}
      error={error}
    />
  );
};

export default WithErrorBoundary(MissionDetails);
